// Shared baseline for every @capacitor/google-maps instance in the app.
// Spread into GoogleMap.create() so all maps follow the same rules
// (no default UI, same Map ID styling, same locale). Per-map overrides
// are applied by spreading after these.

import i18n from 'i18next';
import { isSupportedLanguage, DEFAULT_LANGUAGE } from '../i18n/config';

export const BASE_MAP_CONFIG = {
  mapId: 'DEMO_MAP_ID',
  disableDefaultUI: true,
  // Suppress Google's default POI/address info windows on tap. App maps
  // surface their own selection UI (dish cards, listing cards), so the
  // built-in white-box popup is always wrong here — and on mobile it has
  // no obvious way to dismiss, leaving users stuck.
  clickableIcons: false,
};

// getBaseMapLocale returns { language, region } at map-create time. Language
// follows i18next so a Spanish-locale user sees Spanish POI labels, map
// controls, and info-window UI. Region stays `'US'` — that's a search /
// POI-bias hint, NOT a display-language hint, and a Spanish-speaking US
// user still wants US-biased map data. If we ever ship to MX/ES, region
// should source from the user's country (Profile.country), not language.
//
// Capacitor's GoogleMap.create() locks these in for the map's lifetime —
// changing i18n.language mid-session does NOT re-translate an existing
// map instance. The map picks up the new lang the next time it's
// created (page navigation, modal close+open). See `docs/i18n.md`
// for the broader third-party-components-honoring-locale pattern.
export function getBaseMapLocale(): { language: string; region: string } {
  const lang = i18n.language;
  return {
    language: isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE,
    region: 'US',
  };
}

const METERS_PER_MILE = 1609.344;

/**
 * Circle for a drop-off listing's delivery radius, as the chef sees it.
 * Drop-off gold (`--ion-color-tertiary`, identical in light and dark), the
 * same colour as the drop-off pill, so it reads as "your drop-off area".
 * A literal because the native map takes colours, not CSS variables.
 */
export function dropoffRadiusCircle(center: { lat: number; lng: number }, miles: number) {
  return {
    center,
    radius: miles * METERS_PER_MILE,
    strokeColor: '#B49E31',
    strokeOpacity: 0.85,
    strokeWeight: 2,
    fillColor: '#B49E31',
    fillOpacity: 0.12,
  };
}

/**
 * Web-Mercator zoom at which a circle of `miles` around `lat` fits inside a
 * map element of the given pixel size, with a little margin. Floored so the
 * whole circle is visible, and clamped to a sane range.
 */
export function zoomToFitRadius(lat: number, miles: number, widthPx: number, heightPx: number) {
  const minPx = Math.max(Math.min(widthPx, heightPx), 1);
  const diameterMeters = 2 * miles * METERS_PER_MILE * 1.2;
  const metersPerPxAtZoom0 = 156543.03392 * Math.cos((lat * Math.PI) / 180);
  const zoom = Math.log2((metersPerPxAtZoom0 * minPx) / diameterMeters);
  return Math.max(3, Math.min(15, Math.floor(zoom)));
}
